import { type WorldEventState, WORLD_EVENT_NAMES } from '@shefira/shared';

import { element } from './kit.js';
import { isTouch } from './touch.js';
import { uiFormat, uiText } from './text.js';
import { clockText, type PortraitCache, standingRow } from './world-event-standings.js';
import { clockTitle, joinHint, trackerLine } from './world-event-text.js';
import './world-events.css';

/**
 * A world event under way (ADR 0050), always in sight at the top of the
 * screen: what and where, the time left, its scenario's line (the wave, the
 * giant, the cart's road: `world-event-text.ts`), then the top five
 * defenders with their heads and the player's own line against the
 * threshold. The ranking folds away, remembered for this browser; on a
 * touch screen it starts folded, the screen is small.
 */

const FOLDED_KEY = 'shefira.world-event-folded';
/** The clock is redrawn this often between the server's seconds. */
const TICK_MS = 250;

function readFolded(): boolean {
  try {
    const saved = localStorage.getItem(FOLDED_KEY);
    return saved === null ? isTouch() : saved === '1';
  } catch {
    return isTouch();
  }
}

export class WorldEventTracker {
  private readonly root = element('aside', 'world-event');
  private readonly title = element('div', 'world-event__title');
  private readonly timer = element('div', 'world-event__timer');
  private readonly line = element('div', 'world-event__line');
  private readonly toggle = element('button', 'world-event__toggle');
  private readonly board = element('ol', 'world-event__board');
  private readonly self = element('div', 'world-event__self');
  private state: WorldEventState | null = null;
  private receivedAt = 0;
  private folded = readFolded();
  private readonly clock: number;

  constructor(private readonly portraits: PortraitCache) {
    this.root.hidden = true;
    this.root.setAttribute('role', 'status');
    this.root.setAttribute('aria-label', uiText('Événement mondial'));
    const head = element('div', 'world-event__head');
    head.append(this.title, this.timer);
    this.toggle.type = 'button';
    this.toggle.addEventListener('click', () => {
      this.folded = !this.folded;
      try {
        localStorage.setItem(FOLDED_KEY, this.folded ? '1' : '0');
      } catch {
        /* folded for this visit only */
      }
      this.render();
    });
    this.root.append(head, this.line, this.toggle, this.board, this.self);
    document.body.append(this.root);
    this.clock = window.setInterval(() => {
      this.renderClock();
    }, TICK_MS);
  }

  /** The server's second: the whole state. Null hides the tracker. */
  show(state: WorldEventState | null): void {
    this.state = state;
    this.receivedAt = performance.now();
    this.render();
  }

  /** The heads arrived: draw them. */
  refresh(): void {
    this.render();
  }

  destroy(): void {
    window.clearInterval(this.clock);
    this.root.remove();
  }

  private render(): void {
    const state = this.state;
    this.root.hidden = state === null;
    if (state === null) return;
    this.root.classList.toggle('world-event--folded', this.folded);
    this.title.textContent = uiFormat('{event} · {place}', {
      event: uiText(WORLD_EVENT_NAMES[state.kind]),
      place: state.place,
    });
    const spans = trackerLine(state);
    this.line.replaceChildren(...spans.map((text) => element('span', '', text)));
    this.renderClock();

    const shown = state.phase === 'running' && state.top.length > 0;
    this.toggle.hidden = !shown;
    this.toggle.textContent = uiFormat('Classement ({count})', { count: state.top.length });
    this.toggle.setAttribute('aria-expanded', String(!this.folded));
    this.board.hidden = !shown || this.folded;
    this.board.replaceChildren(
      ...state.top.map((line) =>
        standingRow(line, this.portraits, state.self !== null && line.rank === state.self.rank),
      ),
    );
    this.renderSelf(state);
  }

  /** The player's own line: their share, and how far they are from the reward. */
  private renderSelf(state: WorldEventState): void {
    const self = state.self;
    this.self.hidden = state.phase !== 'running' || self === null;
    if (self === null) return;
    this.self.classList.toggle('world-event__self--eligible', self.eligible);
    const meter = element('div', 'world-event__meter');
    const fill = element('div', 'world-event__meter-fill');
    fill.style.width = `${String(Math.min(100, Math.round((self.score / self.threshold) * 100)))}%`;
    meter.append(fill);
    const text = element('div', 'world-event__self-text');
    if (self.rank === 0) {
      // Not in the ranking yet: the threshold means nothing before the first blow.
      text.append(
        element('span', '', self.fallen ? 'Tombé : tu ne peux plus aider' : joinHint(state)),
      );
      this.self.replaceChildren(text);
      return;
    }
    this.self.classList.toggle('world-event__self--fallen', self.fallen);
    const verdict = self.fallen
      ? self.eligible
        ? 'Tombé · récompense acquise'
        : 'Tombé · hors du combat'
      : self.eligible
        ? 'Récompense acquise'
        : uiFormat('{points} pts pour la récompense', {
            points: Math.max(0, self.threshold - self.score),
          });
    text.append(
      element(
        'span',
        '',
        uiFormat('Toi : {rank}e · {percent} %', { rank: self.rank, percent: self.percent }),
      ),
      element('span', 'world-event__verdict', verdict),
    );
    this.self.replaceChildren(meter, text);
  }

  private renderClock(): void {
    const state = this.state;
    if (state === null) return;
    const left = state.remainingMs - (performance.now() - this.receivedAt);
    this.timer.textContent = clockText(left);
    this.timer.title = clockTitle(state);
  }
}
